import { useMemo, useState } from 'react'
import { scaleBand, scaleLinear } from '@visx/scale'
import type { ChartCanvasProps, HorizontalBarData } from './chartTypes'
import { chartColor, formatTooltipValue } from './visxPrimitives'
import { TooltipCard } from './TooltipCard'

type Active = { id: string; label: string; value: number; secondary?: number; x: number; y: number } | null

export const HorizontalBarChart = ({ data, width, height }: ChartCanvasProps<HorizontalBarData>) => {
  const [active, setActive] = useState<Active>(null)
  const labelWidth = Math.min(140, Math.max(96, width * 0.28))
  const xMax = Math.max(1, width - labelWidth - 46)
  const yScale = useMemo(
    () => scaleBand({ domain: data.bars.map((bar) => bar.id), range: [12, Math.max(28, height - 12)], padding: 0.28 }),
    [data.bars, height],
  )
  const xScale = useMemo(
    () => scaleLinear({ domain: [0, Math.max(1, ...data.bars.map((bar) => bar.value))], range: [0, xMax] }),
    [data.bars, xMax],
  )
  const accent = chartColor(0)
  const secondaryAccent = chartColor(2)

  return (
    <div className="analysisChartCanvas">
      <svg className="analysisChartSvg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Assignee load bars">
        <title>Assignee load</title>
        {data.bars.map((bar) => {
          const y = yScale(bar.id) ?? 0
          const barHeight = yScale.bandwidth()
          const barWidth = xScale(bar.value)
          const secondaryWidth = bar.secondary ? Math.min(barWidth, xScale(bar.secondary)) : 0
          const isActive = active?.id === bar.id
          return (
            <g key={bar.id}>
              <text className="analysisChartAxisText" x="0" y={y + barHeight / 2 + 4}>{bar.label}</text>
              <rect className="analysisChartTrack" x={labelWidth} y={y} width={xMax} height={barHeight} rx="7" />
              <rect
                className="analysisChartBar"
                x={labelWidth}
                y={y}
                width={barWidth}
                height={barHeight}
                rx="7"
                fill={accent}
                fillOpacity={isActive ? 0.95 : 0.78}
                tabIndex={0}
                role="button"
                aria-label={`${bar.label}: ${bar.value} items${bar.secondary !== undefined ? `, ${bar.secondary} overdue` : ''}`}
                onPointerEnter={() => setActive({ id: bar.id, label: bar.label, value: bar.value, secondary: bar.secondary, x: labelWidth + barWidth, y: y + barHeight / 2 })}
                onPointerLeave={() => setActive(null)}
                onFocus={() => setActive({ id: bar.id, label: bar.label, value: bar.value, secondary: bar.secondary, x: labelWidth + barWidth, y: y + barHeight / 2 })}
                onBlur={() => setActive(null)}
              />
              {secondaryWidth > 0 && (
                <rect
                  className="analysisChartBar analysisChartBar--secondary"
                  x={labelWidth}
                  y={y + barHeight * 0.62}
                  width={secondaryWidth}
                  height={barHeight * 0.28}
                  rx="4"
                  fill={secondaryAccent}
                  fillOpacity={0.72}
                />
              )}
              <text className="analysisChartAxisText" x={width - 8} y={y + barHeight / 2 + 4} textAnchor="end">{bar.value}</text>
            </g>
          )
        })}
      </svg>
      {active && (
        <TooltipCard
          left={active.x}
          top={active.y}
          accent={accent}
          title={active.label}
          value={`${formatTooltipValue(active.value)} items`}
          secondary={active.secondary !== undefined ? `${active.secondary} overdue` : undefined}
        />
      )}
    </div>
  )
}
